import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  BackgroundJob,
  CollectionMeta,
  WorkerRun,
  WorkerRunListQuery,
} from '@neuroedge/shared';
import { apiClient, ApiClientError } from './client';

const KEY = ['admin', 'background-jobs'] as const;

/** Job cards - refreshed every minute so "next run" and health stay current. */
export function useBackgroundJobs() {
  return useQuery<BackgroundJob[], ApiClientError>({
    queryKey: KEY,
    queryFn: () => apiClient.get<BackgroundJob[]>('/admin/background-jobs'),
    refetchInterval: 60_000,
  });
}

export function useWorkerRuns(query: WorkerRunListQuery) {
  return useQuery<{ items: WorkerRun[]; meta: CollectionMeta }, ApiClientError>({
    queryKey: [...KEY, 'runs', query],
    queryFn: async () => {
      const envelope = await apiClient.getCollection<WorkerRun>('/admin/background-jobs/runs', {
        query: { ...query },
      });
      return { items: envelope.data, meta: envelope.meta };
    },
    refetchInterval: 60_000,
  });
}

export function useSetWorkerSchedule() {
  const qc = useQueryClient();
  return useMutation<BackgroundJob[], ApiClientError, { worker: string; cron: string }>({
    mutationFn: ({ worker, cron }) =>
      apiClient.put<BackgroundJob[]>(`/admin/background-jobs/${worker}/schedule`, { cron }),
    onSuccess: (data) => qc.setQueryData(KEY, data),
  });
}
